import React from 'react';
import { IAuditoriaSimCapitalResultado } from '../interfaces/IAuditoriaSimCapital';

interface Props {
  resultado: IAuditoriaSimCapitalResultado;
  onVerDetalhe: () => void;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// COR-336: nulo = sem base de Capital Social válida — não deve ser exibido como "0,0000%".
const formatPercentualCapital = (value: number | null | undefined) =>
  value === null || value === undefined ? 'Não determinável' : `${value.toFixed(4)}%`;

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'   ? 'badge-light-danger' :
    status === 'ALERTA' ? 'badge-light-warning' :
                          'badge-light-success';
  return <span className={`badge ${cls}`}>{status}</span>;
};

const TipoBadge: React.FC<{ tipo: string }> = ({ tipo }) => {
  if (tipo === 'RECLASSIFICACAO_PASSIVO_PL')
    return <span className='badge badge-light-warning'>Reclas. Passivo → PL</span>;
  return <span className='badge badge-light-danger'>Aporte Sem Lastro</span>;
};

const AuditoriaResumoSimCapital: React.FC<Props> = ({ resultado, onVerDetalhe }) => {
  if (!resultado || resultado.itens.length === 0) return null;

  const top5 = [...resultado.itens]
    .sort((a, b) => b.divergencia - a.divergencia)
    .slice(0, 5);

  const statusCls =
    resultado.statusGeral === 'ERRO'   ? 'border-danger' :
    resultado.statusGeral === 'ALERTA' ? 'border-warning' :
                                          'border-success';

  return (
    <div className={`card card-custom card-stretch mb-5 border-top border-4 ${statusCls}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-5 mb-1'>
            Auditoria de Simetria — Contas de Capital
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            {resultado.totalErros} erro(s) · {resultado.totalAlertas} alerta(s)
            {resultado.passivoTotal > 0 && (
              <> · Passivo Presumido: <strong className='text-danger'>{formatCurrency(resultado.passivoTotal)}</strong></>
            )}
          </span>
        </h3>
        <div className='card-toolbar'>
          <button className='btn btn-sm btn-light-primary' onClick={onVerDetalhe}>
            Ver Detalhes
          </button>
        </div>
      </div>

      <div className='card-body pt-3'>
        <div className='row g-3 mb-4'>
          <div className='col-md-4'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Erros — Aporte {'>'} 5% Capital</div>
              <div className='fw-bolder fs-3 text-danger'>{resultado.totalErros}</div>
            </div>
          </div>
          <div className='col-md-4'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Alertas — Suspeita / Reclas.</div>
              <div className='fw-bolder fs-3 text-warning'>{resultado.totalAlertas}</div>
            </div>
          </div>
          <div className='col-md-4'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Passivo Presumido (Multa ECD Potencial)</div>
              <div className='fw-bolder fs-5 text-danger'>{formatCurrency(resultado.passivoTotal)}</div>
            </div>
          </div>
        </div>

        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
            <thead>
              <tr className='fw-bolder text-muted'>
                <th className='text-center'>Mês/Ano</th>
                <th className='text-center'>Tipo</th>
                <th className='text-end'>Deb. Caixa</th>
                <th className='text-end'>Créd. PL Total</th>
                <th className='text-end'>Deb. Passivo</th>
                <th className='text-end'>Divergência</th>
                <th className='text-end'>% Capital</th>
                <th className='text-center'>Status</th>
              </tr>
            </thead>
            <tbody>
              {top5.map((item, i) => (
                <tr key={i}>
                  <td className='text-center fw-bold'>{item.mesAno}</td>
                  <td className='text-center'><TipoBadge tipo={item.tipoOcorrencia} /></td>
                  <td className='text-end'>{formatCurrency(item.debitoCaixa)}</td>
                  <td className='text-end fw-bold'>{formatCurrency(item.creditoPLTotal)}</td>
                  <td className='text-end'>{formatCurrency(item.debitoPassivoTotal)}</td>
                  <td className='text-end fw-bolder text-danger'>{formatCurrency(item.divergencia)}</td>
                  <td className='text-end'>{formatPercentualCapital(item.percentualCapital)}</td>
                  <td className='text-center'><StatusBadge status={item.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {resultado.itens.length > 5 && (
          <div className='text-center mt-2'>
            <button className='btn btn-sm btn-light' onClick={onVerDetalhe}>
              Ver todas as {resultado.itens.length} ocorrências
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditoriaResumoSimCapital;
